import { memo, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { NetworkStatus } from "@apollo/client";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

import { ModelType } from "../../../../API";
import { IDateRangeVariable } from "../../../../common/components/datePicker/modelsDatePickers";
import InfiniteScrollWrapper from "../../../../common/components/infinite-scroll/InfiniteScrollWrapper";
import FullPageLoader from "../../../../common/components/item/FullPageLoader";
import TableNoDataOverlay from "../../../../common/components/table/TableNoDataOverlay";
import { AutocompleteOptionType } from "../../../../common/models/autocomplete";
import { RouteEnum } from "../../../../common/models/enums";
import { useGetRecentEvents } from "../../hooks/events/useGetRecentEvents";
import BasicEventTable from "./BasicEventTable";
import GasLeakEventTable from "./GasLeakEventTable";

interface EventTableContainerProps {
  customerId: string;
  facilityId: AutocompleteOptionType;
  modelId: AutocompleteOptionType;
  cameraId?: string;
  dateRange?: IDateRangeVariable | null;
  tagLocationIds?: string[];
}

const EventTableContainer = ({
  customerId,
  modelId,
  facilityId,
  cameraId,
  dateRange,
  tagLocationIds = [],
}: EventTableContainerProps): JSX.Element => {
  const { pathname } = useLocation();

  // When tagLocationIds is set, don't filter by facilityId in API - we'll filter locally
  const shouldUseTagFilter = tagLocationIds.length > 0;

  const {
    events,
    loading,
    fetchNextPage,
    lastEvaluatedKey,
    fetchMoreLoading,
    networkStatus,
  } = useGetRecentEvents({
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: shouldUseTagFilter ? "" : facilityId?.value ?? "",
    deviceId: cameraId ?? "",
    fromDate: dateRange?.from ? dateRange.from.toISOString() : undefined,
    toDate: dateRange?.to ? dateRange.to.toISOString() : undefined,
  });

  const memoizedEvents = useMemo(() => {
    const normalizeDeviceId = (id?: string): string => {
      if (!id) return "";

      return id.startsWith("DE#") ? id.slice(3) : id;
    };

    // Extract location ID from format like "C#customer#L#locationname"
    const normalizeLocationId = (id: string): string => {
      const match = id.match(/L#([^#]+)$/);

      return match ? match[1] : id;
    };

    const baseEvents =
      pathname.includes(RouteEnum.TelemetryHardHat) ||
      pathname.includes(RouteEnum.ControlRoom)
        ? events
        : events.filter(
            item => item.event.modelId !== ModelType.hardhat.toUpperCase()
          );

    let filtered = baseEvents;

    // Filter by tag locations if a tag is selected
    if (tagLocationIds.length > 0) {
      filtered = filtered.filter(({ event }) => {
        const eventLocationId = normalizeLocationId(
          event.facilityId ?? event?.location ?? ""
        );

        return tagLocationIds.some(tagLocId => {
          const normalizedTagLocId = normalizeLocationId(tagLocId);

          return eventLocationId === normalizedTagLocId;
        });
      });
    }

    const selectedDeviceId = cameraId ?? "";

    if (selectedDeviceId) {
      const normalizedSelected = normalizeDeviceId(selectedDeviceId);

      filtered = filtered.filter(({ event, newestRecording }) => {
        const fromEvent = normalizeDeviceId(event.camera_id ?? "");
        const fromRecording = normalizeDeviceId(
          // some records have camera_id in the raw event info nested under data
          // fallback to instance_name if camera_id is missing
          (newestRecording?.rawEventInfo as any)?.data?.camera_id ??
            (newestRecording?.rawEventInfo as any)?.data?.instance_name ??
            ""
        );

        return (
          fromEvent === normalizedSelected ||
          fromRecording === normalizedSelected
        );
      });
    }

    return filtered;
  }, [events, pathname, cameraId, tagLocationIds]);

  if (loading) return <FullPageLoader />;

  if (!memoizedEvents.length && !loading) {
    return <TableNoDataOverlay />;
  }

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <InfiniteScrollWrapper
        fetchNextPage={fetchNextPage}
        lastEvaluatedKey={lastEvaluatedKey}
        loading={loading || fetchMoreLoading}
      >
        {memoizedEvents.map(({ event, newestRecording }) => {
          switch (event.modelId) {
            case "GASLEAK":
              return (
                <GasLeakEventTable
                  event={event}
                  newestRecording={newestRecording}
                  key={event.id}
                />
              );
            default:
              return (
                <BasicEventTable
                  event={event}
                  newestRecording={newestRecording}
                  key={event.id}
                />
              );
          }
        })}
      </InfiniteScrollWrapper>
      {fetchMoreLoading && networkStatus !== NetworkStatus.error && (
        <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Box>
      )}
    </Box>
  );
};

export default memo(EventTableContainer);
